import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const PING_INTERVAL = 2 * 60 * 1000; // 2 minutes in milliseconds
const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes - stop pinging after this

/**
 * Hook that tracks user activity and sends pings to the backend.
 * Only sends pings while the user is actively using the app.
 * 
 * Activity is detected through: mouse movement, clicks, key presses, scrolling, touch
 */
export const useActivityTracker = (userEmail) => {
  const lastActivityRef = useRef(Date.now());
  const pingIntervalRef = useRef(null);
  const isActiveRef = useRef(true);

  // Update last activity timestamp
  const recordActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    isActiveRef.current = true;
  }, []);

  // Send ping to backend using relative URL to avoid CORS issues
  const sendPing = useCallback(async () => {
    if (!userEmail) return;
    
    const timeSinceLastActivity = Date.now() - lastActivityRef.current;
    
    // Only send ping if user was active in the last 5 minutes
    if (timeSinceLastActivity < INACTIVITY_TIMEOUT) {
      try {
        // Use relative URL - works in both preview and production
        await axios.post('/api/audit-logs/activity-ping', null, {
          headers: { 'x-user-email': userEmail }
        });
      } catch (error) {
        // Silently fail - don't disrupt user experience
        console.debug('Activity ping failed:', error.message);
      }
    } else {
      isActiveRef.current = false;
    }
  }, [userEmail]);

  useEffect(() => {
    if (!userEmail) return;

    // Activity event listeners
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Throttled activity handler (max once per second)
    let throttleTimeout = null;
    const throttledRecordActivity = () => {
      if (!throttleTimeout) {
        recordActivity();
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
        }, 1000);
      }
    };

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, throttledRecordActivity, { passive: true });
    });

    // Send initial ping on mount
    sendPing();

    // Set up periodic ping
    pingIntervalRef.current = setInterval(sendPing, PING_INTERVAL);

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledRecordActivity);
      });
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [userEmail, recordActivity, sendPing]);

  return { isActive: isActiveRef.current };
};

export default useActivityTracker;
